import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CalendarPlus, Copy, ExternalLink, Map as MapIcon, MessageSquareText, Navigation, Send, ThumbsDown, ThumbsUp, Trash2, Type } from "lucide-react";
import { useEffect, useState } from "react";
import type { CommentDTO, PlaceCategory, PlaceDTO, PlaceStatus } from "@shared/types";
import { PLACE_CATEGORIES, PLACE_STATUSES } from "@shared/types";
import { del, get, post } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDayShort, fmtSec, relTime, weekday } from "@/lib/format";
import { appleMapsUrl, gmapsDirUrl, gmapsPointUrl } from "@/lib/geo";
import { CATEGORY, PLACE_STATUS } from "@/lib/meta";
import { toast, useUI } from "@/lib/store";
import { tripApi, type Bundle, type Derived } from "@/lib/trip";
import { useChat } from "../chat/chatStore";
import { Avatar, Button, cx, IconButton, Input, Modal, Section, Select, Textarea } from "../ui";

function EditableText({ value, onSave, placeholder, canEdit }: { value: string | null; onSave: (v: string | null) => void; placeholder: string; canEdit: boolean }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  if (edit)
    return (
      <div className="space-y-2">
        <Textarea autoFocus value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} className="min-h-28" />
        <div className="flex justify-end gap-2">
          <Button size="sm" onClick={() => setEdit(false)}>
            Отмена
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              onSave(v.trim() || null);
              setEdit(false);
            }}
          >
            Сохранить
          </Button>
        </div>
      </div>
    );
  return (
    <button disabled={!canEdit} onClick={() => setEdit(true)} className="w-full rounded-xl px-1 py-1 text-left text-[14px] leading-relaxed whitespace-pre-wrap hover:bg-[var(--surface-2)]">
      {value || <span className="text-muted">{placeholder}</span>}
    </button>
  );
}

function Comments({ tripId, placeId, canWrite }: { tripId: string; placeId: string; canWrite: boolean }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["comments", tripId, placeId], queryFn: () => get<CommentDTO[]>(`/api/trips/${tripId}/comments?target=${placeId}`) });
  const [text, setText] = useState("");
  const b = qc.getQueryData<Bundle>(["trip", tripId]);
  const members = new Map(b?.members.map((m) => [m.id, m]) ?? []);
  const send = async () => {
    if (!text.trim()) return;
    await post(`/api/trips/${tripId}/comments`, { targetType: "place", targetId: placeId, body: text.trim() });
    setText("");
    void q.refetch();
  };
  return (
    <div className="space-y-2">
      {q.data?.map((c) => {
        const m = members.get(c.userId);
        return (
          <div key={c.id} className="flex gap-2">
            <Avatar m={m ?? { name: "?", email: null, avatarUrl: null }} size={26} />
            <div className="min-w-0 flex-1 rounded-2xl bg-[var(--surface-2)] px-3 py-2">
              <div className="flex items-center gap-2 text-[12px]">
                <b>{m?.name ?? m?.email ?? "Участник"}</b>
                <span className="text-muted">{relTime(c.createdAt)}</span>
                {c.userId === user?.id && (
                  <button
                    className="ml-auto text-muted hover:text-[var(--danger)]"
                    onClick={async () => {
                      await del(`/api/trips/${tripId}/comments/${c.id}`);
                      void q.refetch();
                    }}
                  >
                    удалить
                  </button>
                )}
              </div>
              <div className="text-[14px] whitespace-pre-wrap">{c.body}</div>
            </div>
          </div>
        );
      })}
      <div className={cx("flex gap-2", !canWrite && "hidden")}>
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Написать комментарий…" onKeyDown={(e) => e.key === "Enter" && send()} />
        <IconButton label="Отправить" onClick={send} className="size-10 bg-[var(--accent)] text-[var(--accent-fg)] hover:bg-[var(--accent)]">
          <Send size={16} />
        </IconButton>
      </div>
    </div>
  );
}

export function PlaceDetails({ b, d, place, onOpenSource }: { b: Bundle; d: Derived; place: PlaceDTO; onOpenSource: (sourceId: string, t?: number | null) => void }) {
  const { user } = useAuth();
  const selectPlace = useUI((s) => s.selectPlace);
  const set = useUI((s) => s.set);
  const askChat = useChat((s) => s.prefill);
  const tripId = b.trip.id;
  const canEdit = b.trip.myRole !== "viewer";
  const [driver, setDriver] = useState(false);
  const [dayPick, setDayPick] = useState("");
  const links = useQuery({
    queryKey: ["placeSources", tripId, place.id],
    queryFn: () => get<{ sourceId: string; timestampSec: number | null }[]>(`/api/trips/${tripId}/places/${place.id}/sources`),
  });
  const days = d.placeDays.get(place.id) ?? [];
  const items = b.items.filter((x) => x.placeId === place.id);
  const myVote = place.votes?.find((v) => v.userId === user?.id)?.value ?? 0;
  const ups = place.votes?.filter((v) => v.value > 0) ?? [];
  const downs = place.votes?.filter((v) => v.value < 0) ?? [];
  const widgets = b.widgets.filter((w) => w.targetType === "place" && w.targetId === place.id);
  const cat = CATEGORY[place.category];

  const upd = (p: Partial<PlaceDTO>) => tripApi.updatePlace(tripId, place.id, p).catch((e) => toast((e as Error).message, { tone: "error" }));

  const addToDay = async (day: string) => {
    if (!day) return;
    await tripApi.createItem(tripId, { day, kind: place.category === "hotel" ? "stay" : "visit", placeId: place.id, title: place.name, nights: place.category === "hotel" ? 1 : null });
    if (place.status === "idea") await upd({ status: "want" });
    setDayPick("");
    toast(`Добавлено в ${fmtDayShort(day)}`);
  };

  const vote = (v: -1 | 1) => tripApi.vote(tripId, place.id, myVote === v ? 0 : v).catch((e) => toast((e as Error).message, { tone: "error" }));
  const copy = (t: string) => navigator.clipboard?.writeText(t).then(() => toast("Скопировано"));

  return (
    <div className="animate-in pb-6">
      <div className="mb-3 flex items-start gap-2">
        <IconButton label="Назад" onClick={() => selectPlace(null)}>
          <ArrowLeft size={18} />
        </IconButton>
        <div className="grid size-12 shrink-0 place-items-center rounded-2xl text-2xl" style={{ background: `color-mix(in srgb, ${cat.color} 15%, transparent)` }}>
          {cat.emoji}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-[18px] leading-tight font-extrabold">{place.name}</h2>
          {place.nameLocal && <div className="text-[13px] font-medium text-muted">{place.nameLocal}</div>}
          <div className="text-[12px] text-muted">
            {cat.label}
            {place.region ? ` · ${place.region}` : ""}
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {PLACE_STATUSES.map((s) => (
          <button
            key={s}
            disabled={!canEdit}
            onClick={() => upd({ status: s as PlaceStatus })}
            className={cx(
              "h-8 rounded-full border px-3 text-[13px] font-semibold",
              place.status === s ? "border-[var(--accent)] bg-[var(--accent)] text-[var(--accent-fg)]" : "border-[var(--border)] hover:border-[var(--border-strong)]",
            )}
          >
            {PLACE_STATUS[s].label}
          </button>
        ))}
        <div className={cx("ml-auto flex items-center gap-1", !canEdit && "pointer-events-none opacity-60")}>
          <button onClick={() => vote(1)} className={cx("flex h-8 items-center gap-1 rounded-full border px-2.5 text-[13px] font-bold", myVote > 0 ? "border-jade-500 bg-jade-100 text-jade-800 dark:bg-jade-400/20 dark:text-jade-200" : "border-[var(--border)]")}>
            <ThumbsUp size={14} /> {ups.length || ""}
          </button>
          <button onClick={() => vote(-1)} className={cx("flex h-8 items-center gap-1 rounded-full border px-2.5 text-[13px] font-bold", myVote < 0 ? "border-rose-400 bg-rose-100 text-rose-700 dark:bg-rose-400/20 dark:text-rose-200" : "border-[var(--border)]")}>
            <ThumbsDown size={14} /> {downs.length || ""}
          </button>
        </div>
      </div>
      {ups.length > 0 && (
        <div className="-mt-2 mb-4 flex items-center gap-1 text-[12px] text-muted">
          <span className="flex -space-x-1.5">
            {ups.map((v) => {
              const m = d.members.get(v.userId);
              return m ? <Avatar key={v.userId} m={m} size={20} ring /> : null;
            })}
          </span>
          хотят сюда
        </div>
      )}

      <div className="mb-4 grid grid-cols-4 gap-2">
        {[
          { icon: <Navigation size={18} />, label: "Маршрут", href: gmapsDirUrl(place.lat, place.lng) },
          { icon: <ExternalLink size={18} />, label: "Google", href: gmapsPointUrl(place.lat, place.lng) },
          { icon: <MapIcon size={18} />, label: "Apple", href: appleMapsUrl(place.lat, place.lng, place.name) },
        ].map((x) => (
          <a key={x.label} href={x.href} target="_blank" rel="noreferrer" className="surface flex flex-col items-center gap-1 rounded-2xl py-2.5 text-[11px] font-semibold hover:bg-[var(--surface-2)]">
            {x.icon}
            {x.label}
          </a>
        ))}
        <button onClick={() => copy(`${place.lat.toFixed(6)}, ${place.lng.toFixed(6)}`)} className="surface flex flex-col items-center gap-1 rounded-2xl py-2.5 text-[11px] font-semibold hover:bg-[var(--surface-2)]">
          <Copy size={18} />
          Координаты
        </button>
      </div>
      {(place.nameLocal || place.address) && (
        <Button variant="soft" className="mb-4 w-full" icon={<Type size={16} />} onClick={() => setDriver(true)}>
          Показать водителю (крупно)
        </Button>
      )}

      <Section title="В плане">
        {items.length ? (
          <div className="space-y-1">
            {items.map((it) => {
              const di = d.dayIndex.get(it.day);
              return (
                <button
                  key={it.id}
                  onClick={() => set({ selectedDay: it.day, selectedItemId: it.id, tab: "plan", selectedPlaceId: null })}
                  className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm hover:bg-[var(--surface-2)]"
                >
                  <span className="size-2.5 rounded-full" style={{ background: di?.color ?? "#999" }} />
                  <b>
                    {weekday(it.day)}, {fmtDayShort(it.day)}
                  </b>
                  <span className="truncate text-muted">
                    {it.startTime ? `${it.startTime} · ` : ""}
                    {it.title}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-[13px] text-muted">Ещё не в плане.</div>
        )}
        {canEdit && (
          <div className="mt-2 flex gap-2">
            <Select value={dayPick} onChange={(e) => setDayPick(e.target.value)}>
              <option value="">Добавить в день…</option>
              {d.days.map((x) => (
                <option key={x.day} value={x.day}>
                  {weekday(x.day)}, {fmtDayShort(x.day)} {x.base ? `· ${x.base}` : ""} {days.includes(x.day) ? "✓" : ""}
                </option>
              ))}
            </Select>
            <Button variant="primary" className="shrink-0 whitespace-nowrap" disabled={!dayPick} icon={<CalendarPlus size={16} />} onClick={() => addToDay(dayPick)}>
              В план
            </Button>
          </div>
        )}
      </Section>

      <Section title="Описание">
        <EditableText value={place.description} canEdit={canEdit} placeholder="Добавить описание…" onSave={(v) => upd({ description: v })} />
      </Section>
      <Section title="Наши заметки">
        <EditableText value={place.notes} canEdit={canEdit} placeholder="Цены, часы, что заказать, советы друзей…" onSave={(v) => upd({ notes: v })} />
      </Section>

      {(links.data?.length ?? 0) > 0 && (
        <Section title="Откуда узнали">
          <div className="space-y-1.5">
            {links.data!.map((l) => {
              const s = b.sources.find((x) => x.id === l.sourceId);
              if (!s) return null;
              return (
                <button key={l.sourceId} onClick={() => onOpenSource(s.id, l.timestampSec)} className="surface flex w-full items-center gap-2 rounded-xl p-2 text-left hover:bg-[var(--surface-2)]">
                  {s.thumbnailUrl ? <img src={s.thumbnailUrl} alt="" className="h-10 w-16 shrink-0 rounded-lg object-cover" /> : <span className="text-xl">📰</span>}
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-[13px] font-semibold">{s.title ?? s.url}</span>
                    {l.timestampSec != null && <span className="text-[12px] font-bold text-[var(--accent)]">▶ {fmtSec(l.timestampSec)}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </Section>
      )}

      {widgets.length > 0 && (
        <Section title="Исследования ИИ">
          <div className="space-y-1.5">
            {widgets.map((w) => (
              <button key={w.id} onClick={() => set({ widgetOpenId: w.id })} className="surface flex w-full items-center gap-2 rounded-xl p-2.5 text-left hover:bg-[var(--surface-2)]">
                <span className="text-xl">🧩</span>
                <span className="min-w-0 flex-1 truncate text-[14px] font-semibold">{w.title}</span>
              </button>
            ))}
          </div>
        </Section>
      )}

      <Section title="Обсуждение">
        <Comments tripId={tripId} placeId={place.id} canWrite={canEdit} />
      </Section>

      <Section title="Детали">
        <div className="grid grid-cols-2 gap-2">
          <Select disabled={!canEdit} value={place.category} onChange={(e) => upd({ category: e.target.value as PlaceCategory })}>
            {PLACE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY[c].emoji} {CATEGORY[c].label}
              </option>
            ))}
          </Select>
          <Input disabled={!canEdit} defaultValue={place.region ?? ""} placeholder="Регион" onBlur={(e) => e.target.value !== (place.region ?? "") && upd({ region: e.target.value || null })} />
          <Input disabled={!canEdit} defaultValue={place.url ?? ""} placeholder="Сайт / ссылка" className="col-span-2" onBlur={(e) => e.target.value !== (place.url ?? "") && upd({ url: e.target.value || null })} />
        </div>
        {place.url && (
          <a href={place.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[var(--accent)]">
            <ExternalLink size={14} /> Открыть ссылку
          </a>
        )}
      </Section>

      <div className="flex flex-wrap gap-2">
        <Button variant="soft" icon={<MessageSquareText size={16} />} onClick={() => askChat(`Расскажи про «${place.name}» {place:${place.id.slice(0, 8)}}: стоит ли включать в план, когда лучше идти и что рядом?`)}>
          Спросить ИИ
        </Button>
        {canEdit && (
          <Button
            variant="ghost"
            className="text-[var(--danger)]"
            icon={<Trash2 size={16} />}
            onClick={async () => {
              const r = await tripApi.deletePlace(tripId, place.id);
              selectPlace(null);
              toast(`«${place.name}» удалено`, { action: r.activityId ? { label: "Вернуть", run: () => void tripApi.undo(tripId, { activityId: r.activityId! }) } : undefined });
            }}
          >
            Удалить место
          </Button>
        )}
      </div>

      <Modal open={driver} onClose={() => setDriver(false)} title="Для водителя">
        <div className="py-6 text-center">
          <div className="font-display text-[34px] leading-tight font-bold">{place.nameLocal ?? place.name}</div>
          {place.address && <div className="mt-4 text-[20px] leading-snug">{place.address}</div>}
          <div className="mt-4 text-[15px] text-muted">
            {place.lat.toFixed(6)}, {place.lng.toFixed(6)}
          </div>
        </div>
      </Modal>
    </div>
  );
}
